import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Modal, Select, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { hasPermission } from "../store/userSlice";
import { vehiclesNotAssignedToClient, assignVehicleToClient } from "../api/vehicle/vehicleRequest";

const { Option } = Select;
const PAGE_SIZE = 20;

export default function AssignVehicle({ clientId, onAssigned }) {
  const [visible, setVisible] = useState(false);
  const [allVehicles, setAllVehicles] = useState([]);       // cached from server
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const searchRef = useRef("");
  const pageRef = useRef(1);

  const canAssign = useSelector((state) => hasPermission(state, "vehicles", "update"));

  const fetchVehicles = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const res = await vehiclesNotAssignedToClient({ page, per_page: PAGE_SIZE, search });
      const vehicles = res.data || [];
      const total = res.total || 0;

      if (page === 1) {
        setAllVehicles(vehicles);
      } else {
        setAllVehicles((prev) => [...prev, ...vehicles]);
      }
      setHasMore(page * PAGE_SIZE < total);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors du chargement des véhicules disponibles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchAvailable = async () => {
      if (!visible || !canAssign) return;
      // Reset state on modal open
      searchRef.current = "";
      pageRef.current = 1;
      setSearchText("");
      setAllVehicles([]);
      setHasMore(true);
      await fetchVehicles(1, "");
    };

    fetchAvailable();
  }, [visible, canAssign, fetchVehicles]);

  if (!canAssign) return null;

  const showModal = () => setVisible(true);
  const handleCancel = () => {
    setVisible(false);
    setSelectedVehicle(null);
    setSearchText("");
  };

  // When user types: if >= 3 chars, fetch from server with that search
  // If user backspaces below 3 chars, filter from cached data
  const handleSearch = (value) => {
    setSearchText(value);

    if (value.length >= 3) {
      // Fetch fresh results from server
      pageRef.current = 1;
      searchRef.current = value;
      setAllVehicles([]);
      fetchVehicles(1, value);
    } else if (value.length === 0) {
      // Reset to full list
      pageRef.current = 1;
      searchRef.current = "";
      setAllVehicles([]);
      fetchVehicles(1, "");
    }
    // If 1-2 chars, do nothing — filter from cached data below
  };

  // Client-side filter: when searchText is 1-2 chars, filter allVehicles
  const filteredVehicles = searchText.length >= 1 && searchText.length < 3
    ? allVehicles.filter((v) =>
        v.matricule.toLowerCase().includes(searchText.toLowerCase())
      )
    : allVehicles;

  const handleAssign = async () => {
    if (!selectedVehicle) {
      message.warning("Veuillez sélectionner un véhicule.");
      return;
    }
    try {
      await assignVehicleToClient(clientId, selectedVehicle);
      message.success("Véhicule assigné avec succès");
      setVisible(false);
      setSelectedVehicle(null);
      setSearchText("");
      if (typeof onAssigned === "function") onAssigned();
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'assignation du véhicule");
    }
  };

  // Load more on scroll (virtual infinite scroll for Select)
  const handlePopupScroll = (e) => {
    const { target } = e;
    // Load more when scrolled near bottom
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
      if (hasMore && !loading) {
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        fetchVehicles(nextPage, searchRef.current);
      }
    }
  };

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={showModal} style={{ borderRadius: "4px" }}>
        Assigner un véhicule
      </Button>

      <Modal
        title="Assigner un véhicule"
        open={visible}
        onOk={handleAssign}
        onCancel={handleCancel}
        okText="Assigner"
        cancelText="Annuler"
        width={500}
      >
        <Select
          placeholder="Rechercher par matricule (min. 3 caractères)"
          style={{ width: "100%" }}
          onChange={(value) => setSelectedVehicle(value)}
          onSearch={handleSearch}
          showSearch
          optionFilterProp="children"
          value={selectedVehicle}
          loading={loading}
          filterOption={false}
          notFoundContent={loading ? null : "Aucun véhicule trouvé"}
          onPopupScroll={handlePopupScroll}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
        >
          {filteredVehicles.map((vehicle) => (
            <Option key={vehicle.id} value={vehicle.id}>
              {`${vehicle.matricule} - ${vehicle.brand || ""} ${vehicle.model || ""}`.trim()}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
}
